import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useContext, useEffect, useMemo, useState } from 'react';
import { ThemeContext } from 'styled-components';
import { status } from 'constants/general';

import Modal from 'modules/common/components/Modal/Modal';
import SectionDivider from 'modules/common/components/SectionDivider';
import DropdownMenu from 'modules/common/components/DropdownMenu';
import SearchBar from 'modules/common/components/SearchBar/SearchBar';
import NewButton from 'modules/common/components/Button/Button';

import healthPlansStrings from 'modules/healthPlans/strings';

import initialIcon from 'assets/images/empty-health-plans.svg';

import Table from 'modules/common/components/Table';
import { TableItemType } from 'modules/common/components/Table/enums';
import {
  Container,
  Center,
  Img,
  Text,
  Button,
  InputWrapper,
  InputLabel,
  Input,
  extedntModalContentStyle,
  extendModalTitleStyle,
  extendModalBodyStyle,
  ModalContainer,
  ButtonsContainer,
  CancelButton,
  Wrapper,
  extendBlueButtonStyle,
  extendLoaderStyle,
} from './HealthPlansComponent.style';

const HealthPlansComponent = ({
  isCreateHealthPlanModalHidden,
  setIsCreateHealthPlanModalHidden,
  formFieldsValues,
  formFieldsValidation,
  handleSubmitHealthPlan,
  handleInputFieldChange,
  searchQuery,
  handleSearchBarInputChange,
  handleShowMoreClick,
  handleHealthPlanEdit,
  handleHealthPlanDeactivate,
  handleCancelHealthPlan,
}) => {
  const themeContext = useContext(ThemeContext);
  const { colors } = themeContext;
  const { grey003 } = colors;

  const [healthPlansRecords, setHealthPlansRecords] = useState([]);

  const healthPlans = useSelector(state => state.healthPlans.healthPlansList);
  const insuranceCompanies = useSelector(state => state.healthPlans.insurance.data);
  const createHealthPlanStatus = useSelector(state => state.healthPlans.createHealthPlan.status);
  const editHealthPlanStatus = useSelector(state => state.healthPlans.editHealthPlan.status);
  const countries = useSelector(state => Object.values(state.common.countries.data)).map(item => ({
    key: item.id,
    name: item.name,
  }));

  const isSubmitting =
    createHealthPlanStatus === status.FETCHING || editHealthPlanStatus === status.FETCHING;

  const insuranceCompaniesListData = useMemo(
    () =>
      insuranceCompanies.map(item => ({
        key: item.id,
        name: item.name,
      })),
    [insuranceCompanies],
  );

  const getMainRowItems = () => {
    const rows = [];
    healthPlans.data.forEach(item => {
      const { id, name, insuranceCompany, active } = item;
      rows.push({
        id,
        rowData: item,
        rowContent: [
          {
            itemContent: name,
            itemType: TableItemType.Link,
            itemPath: `${item.id}`,
            isItemClickable: false,
          },
          {
            itemContent: insuranceCompany.name,
            itemType: TableItemType.Text,
            isItemClickable: false,
          },
          {
            itemContent: active,
            itemType: TableItemType.Status,
            isItemClickable: false,
          },
        ],
        editButtons: [
          {
            btnId: 1,
            btnIcon: '"\\e90a"',
            btnLabel: 'Edit',
            btnFn: () => handleHealthPlanEdit(item),
          },
          {
            btnId: 2,
            btnIcon: active ? '"\\e91f"' : '"\\e908"',
            btnLabel: active ? 'Deactivate' : 'Activate',
            btnFn: () => handleHealthPlanDeactivate(item),
          },
        ],
      });
    });

    return rows;
  };

  const validateHealthPlantData = () => {
    let isValid = true;
    if (!formFieldsValues.healthPlan.trim()) isValid = false;
    // if (!formFieldsValues.insurance) isValid = false; // Insurance section commented out
    if (!formFieldsValues.country) isValid = false;
    return isValid;
  };

  useEffect(() => {
    setHealthPlansRecords(() => getMainRowItems());
  }, [healthPlans.data]);

  const selectedInsuranceObj =
    insuranceCompanies
      ?.filter(item => item.id === formFieldsValues.insurance)
      .map(item => ({
        key: item.key,
        name: item.name,
      }))[0] || {};

  const selectedCountryObj =
    countries.filter(item => item.key === formFieldsValues.country)[0] || {};

  return (
    <Container>
      {healthPlans.status === status.SUCCESS && healthPlans.data.length === 0 && !searchQuery ? (
        <Center>
          <Img src={initialIcon} />
          <Text>{healthPlansStrings.healthPlan.emptyState}</Text>
          <Button onClick={() => setIsCreateHealthPlanModalHidden(false)}>
            {healthPlansStrings.healthPlan.createHealthPlan}
          </Button>
        </Center>
      ) : (
        <Wrapper>
          <Button
            style={{ marginLeft: 'auto' }}
            onClick={() => setIsCreateHealthPlanModalHidden(false)}
          >
            {healthPlansStrings.healthPlan.createHealthPlan}
          </Button>
          <SearchBar
            searchQuery={searchQuery}
            handleSearchBarInputChange={handleSearchBarInputChange}
            testId="healthplansearch"
          />
          <Table
            latestDataLength={healthPlans.latestDataLength}
            dataStatus={healthPlans.status}
            columns={healthPlansStrings.healthPlanList.gridHeaderList}
            rows={healthPlansRecords}
            searchQuery={searchQuery}
            handleEditButton={handleHealthPlanEdit}
            handleShowMoreClick={handleShowMoreClick}
          />
        </Wrapper>
      )}
      <Modal
        extendModalContent={extedntModalContentStyle}
        extendTitleStyle={extendModalTitleStyle}
        extendModalBody={extendModalBodyStyle}
        title={
          formFieldsValues.key
            ? healthPlansStrings.healthPlan.editPlan
            : healthPlansStrings.healthPlan.createPlan
        }
        isModalHidden={isCreateHealthPlanModalHidden}
        onCloseModal={() => handleCancelHealthPlan()}
      >
        <ModalContainer>
          <InputWrapper>
            <InputLabel as="label">Country</InputLabel>
            <DropdownMenu
              dropdownMenuItemsList={countries}
              dropdownMenuSelectedObj={selectedCountryObj}
              dropdownMenuPlaceholder="Country"
              showLoader={false}
              disabled={!!formFieldsValues.key}
              handleDropdownMenuChange={value => handleInputFieldChange('country', value)}
            />
          </InputWrapper>
          <SectionDivider gap={16} />
          <InputWrapper>
            <InputLabel as="label">{healthPlansStrings.healthPlan.healthPlan}</InputLabel>
            <Input
              type="text"
              placeholder={healthPlansStrings.healthPlan.healthPlanPlaceholder}
              value={formFieldsValues.healthPlan}
              onChange={event => handleInputFieldChange('healthPlan', event)}
              error={!!formFieldsValidation?.healthPlan?.errMessage}
            />
          </InputWrapper>
          {/* <SectionDivider gap={16} />
          <InputWrapper>
            <InputLabel as="label">{healthPlansStrings.healthPlan.insurance}</InputLabel>
            <DropdownMenu
              dropdownMenuItemsList={insuranceCompaniesListData}
              dropdownMenuSelectedObj={selectedInsuranceObj}
              dropdownMenuPlaceholder={healthPlansStrings.healthPlan.insurancePlaceholder}
              showLoader={false}
              handleDropdownMenuChange={value => handleInputFieldChange('insurance', value)}
            />
          </InputWrapper> */}
        </ModalContainer>
        <SectionDivider withSeparator gap={1} borderColor={grey003} />
        <ButtonsContainer>
          <CancelButton onClick={() => handleCancelHealthPlan()}>
            {healthPlansStrings.healthPlan.cancel}
          </CancelButton>
          <NewButton
            onClick={() => {
              handleSubmitHealthPlan();
            }}
            btnText={
              formFieldsValues.key
                ? healthPlansStrings.healthPlan.editPlan
                : healthPlansStrings.healthPlan.createPlan
            }
            extendButtonStyle={extendBlueButtonStyle}
            key="save-btn"
            disabled={!validateHealthPlantData()}
            extendLoaderStyle={extendLoaderStyle}
            isLoading={isSubmitting}
          />
        </ButtonsContainer>
      </Modal>
    </Container>
  );
};

HealthPlansComponent.propTypes = {
  isCreateHealthPlanModalHidden: PropTypes.bool.isRequired,
  setIsCreateHealthPlanModalHidden: PropTypes.func.isRequired,
  formFieldsValues: PropTypes.shape({
    insurance: PropTypes.string,
    healthPlan: PropTypes.string,
    country: PropTypes.string,
  }).isRequired,
  formFieldsValidation: PropTypes.shape({
    insurance: PropTypes.bool,
    healthPlan: PropTypes.bool,
  }).isRequired,
  handleSubmitHealthPlan: PropTypes.func.isRequired,
  handleInputFieldChange: PropTypes.func.isRequired,
  searchQuery: PropTypes.string.isRequired,
  handleSearchBarInputChange: PropTypes.func.isRequired,
  healthplansQueryString: PropTypes.shape({
    pagenum: PropTypes.number,
    pagesize: PropTypes.number,
    includetotalcount: PropTypes.bool,
  }).isRequired,
  handleHealthPlanEdit: PropTypes.func.isRequired,
  handleCancelHealthPlan: PropTypes.func.isRequired,
};

export default HealthPlansComponent;
